import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, Plus, Trash2, CalendarRange, Settings2, X, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { retentionAPI } from '@/lib/api';

const CATEGORY_OPTIONS = [
  { value: 'aca', label: 'ACA' },
  { value: 'medicare', label: 'Medicare' },
  { value: 'medicare_advantage', label: 'Medicare Advantage' },
  { value: 'life', label: 'Life' },
  { value: 'other', label: 'Other' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// KR-Admin only: configure enrollment seasons + retention rules so dates
// can change year to year without code changes.
export default function AdminRetentionRules() {
  const [seasons, setSeasons] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, cfg] = await Promise.all([retentionAPI.seasons(), retentionAPI.settings()]);
      setSeasons(s.data?.seasons || []);
      setSettings(cfg.data || {});
    } catch {
      toast.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-retention-rules">
      {/* Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Settings2 size={16} className="text-slate-500" /> Retention Rules</CardTitle>
        </CardHeader>
        <CardContent>
          <SettingsForm settings={settings} onSaved={load} />
        </CardContent>
      </Card>

      {/* Cross-sell mapping */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><TrendingUp size={16} className="text-emerald-600" /> Cross-Sell Mapping</CardTitle>
        </CardHeader>
        <CardContent>
          <CrossSellMapForm settings={settings} onSaved={load} />
        </CardContent>
      </Card>

      {/* Seasons */}
      <Card>
        <CardHeader className="pb-3 flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><CalendarRange size={16} className="text-slate-500" /> Enrollment Seasons</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setSeasons((s) => [...s, { _new: true, name: '', start_month: 1, start_day: 1, end_month: 1, end_day: 31, categories: [], early_days: 30, active: true }])} data-testid="add-season-btn">
            <Plus size={14} className="mr-1" /> Add season
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {seasons.map((s, i) => (
            <SeasonRow key={s.season_id || `new-${i}`} season={s} onSaved={load} />
          ))}
          {seasons.length === 0 && <p className="text-sm italic text-slate-400 text-center py-4">No seasons configured.</p>}
        </CardContent>
      </Card>
    </div>
  );
}

function SettingsForm({ settings, onSaved }) {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await retentionAPI.updateSettings({
        no_contact_months: Number(form.no_contact_months),
        extended_no_contact_months: Number(form.extended_no_contact_months),
        annual_review_early_days: Number(form.annual_review_early_days),
        cross_sell_enabled: !!form.cross_sell_enabled,
      });
      toast.success('Rules saved');
      onSaved();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <NumField label="Needs-contact after (months)" value={form.no_contact_months} onChange={(v) => upd('no_contact_months', v)} testid="rule-no-contact" />
        <NumField label="At-risk silence (months)" value={form.extended_no_contact_months} onChange={(v) => upd('extended_no_contact_months', v)} testid="rule-ext-contact" />
        <NumField label="Annual review lead (days)" value={form.annual_review_early_days} onChange={(v) => upd('annual_review_early_days', v)} testid="rule-annual-days" />
      </div>
      <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
        <div>
          <p className="text-sm font-medium text-slate-700">Cross-sell opportunities</p>
          <p className="text-xs text-slate-500">Surface product-gap opportunities for active clients</p>
        </div>
        <Switch checked={!!form.cross_sell_enabled} onCheckedChange={(v) => upd('cross_sell_enabled', v)} data-testid="rule-crosssell-toggle" />
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-rules-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save rules
        </Button>
      </div>
    </div>
  );
}

const NumField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-xs text-slate-500">{label}</label>
    <Input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="h-9" data-testid={testid} />
  </div>
);

const CROSS_SELL_CATEGORIES = [
  { key: 'medicare', label: 'Medicare' },
  { key: 'medicare_advantage', label: 'Medicare Advantage' },
  { key: 'aca', label: 'ACA' },
  { key: 'life', label: 'Life' },
  { key: 'other', label: 'Other' },
];

// KR-Admin editor for the cross-sell mapping: for each product category
// the admin controls which gap products are suggested to active clients.
function CrossSellMapForm({ settings, onSaved }) {
  const [map, setMap] = useState(() => {
    const src = settings?.cross_sell_map || {};
    const out = {};
    CROSS_SELL_CATEGORIES.forEach((c) => { out[c.key] = Array.isArray(src[c.key]) ? [...src[c.key]] : []; });
    return out;
  });
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  const addProduct = (cat) => {
    const val = (drafts[cat] || '').trim();
    if (!val) return;
    setMap((m) => (m[cat].some((p) => p.toLowerCase() === val.toLowerCase()) ? m : { ...m, [cat]: [...m[cat], val] }));
    setDrafts((d) => ({ ...d, [cat]: '' }));
  };
  const removeProduct = (cat, prod) => setMap((m) => ({ ...m, [cat]: m[cat].filter((p) => p !== prod) }));

  const save = async () => {
    setSaving(true);
    try {
      await retentionAPI.updateSettings({ cross_sell_map: map });
      toast.success('Cross-sell mapping saved');
      onSaved();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="cross-sell-map-form">
      <p className="text-xs text-slate-500">
        When an active client holds a product in a category, these gap products are suggested as potential cross-sell opportunities (products the client already holds are skipped automatically).
      </p>
      {CROSS_SELL_CATEGORIES.map((c) => (
        <div key={c.key} className="rounded-lg border border-slate-200 p-3" data-testid={`crosssell-cat-${c.key}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">{c.label}</span>
            <span className="text-[11px] text-slate-400">{map[c.key].length} product{map[c.key].length === 1 ? '' : 's'}</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            {map[c.key].length === 0 && <span className="text-xs italic text-slate-400">No suggestions</span>}
            {map[c.key].map((prod) => (
              <span key={prod} className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full pl-2 pr-1 py-0.5" data-testid={`crosssell-chip-${c.key}-${prod}`}>
                {prod}
                <button onClick={() => removeProduct(c.key, prod)} className="hover:text-rose-600" data-testid={`crosssell-remove-${c.key}-${prod}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={drafts[c.key] || ''}
              onChange={(e) => setDrafts((d) => ({ ...d, [c.key]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addProduct(c.key); } }}
              placeholder="Add a product (e.g. Dental)"
              className="h-8 text-sm max-w-xs"
              data-testid={`crosssell-input-${c.key}`}
            />
            <Button size="sm" variant="outline" className="h-8" onClick={() => addProduct(c.key)} data-testid={`crosssell-add-${c.key}`}>
              <Plus size={13} className="mr-1" /> Add
            </Button>
          </div>
        </div>
      ))}
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-crosssell-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save mapping
        </Button>
      </div>
    </div>
  );
}

function SeasonRow({ season, onSaved }) {
  const [s, setS] = useState(season);
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const toggleCat = (c) => setS((x) => {
    const has = (x.categories || []).includes(c);
    return { ...x, categories: has ? x.categories.filter((y) => y !== c) : [...(x.categories || []), c] };
  });

  const save = async () => {
    if (!s.name) { toast.error('Season name required'); return; }
    setSaving(true);
    const payload = {
      name: s.name,
      start_month: Number(s.start_month), start_day: Number(s.start_day),
      end_month: Number(s.end_month), end_day: Number(s.end_day),
      categories: s.categories || [], early_days: Number(s.early_days), active: !!s.active,
    };
    try {
      if (s.season_id) await retentionAPI.updateSeason(s.season_id, payload);
      else await retentionAPI.createSeason(payload);
      toast.success('Season saved');
      onSaved();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!s.season_id) { onSaved(); return; }
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await retentionAPI.deleteSeason(s.season_id);
      toast.success('Season deleted');
      onSaved();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid={`season-row-${s.season_id || 'new'}`}>
      <div className="flex items-center gap-2">
        <Input value={s.name} onChange={(e) => upd('name', e.target.value)} placeholder="Season name" className="h-9 font-medium flex-1" data-testid="season-name" />
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Active</span>
          <Switch checked={!!s.active} onCheckedChange={(v) => upd('active', v)} data-testid="season-active" />
        </div>
        <button onClick={del} className="text-rose-500 hover:text-rose-700 p-1" data-testid="season-delete"><Trash2 size={15} /></button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <MonthDay label="Start" m={s.start_month} d={s.start_day} onM={(v) => upd('start_month', v)} onD={(v) => upd('start_day', v)} />
        <MonthDay label="End" m={s.end_month} d={s.end_day} onM={(v) => upd('end_month', v)} onD={(v) => upd('end_day', v)} />
        <div>
          <label className="text-[11px] text-slate-500">Appear (days early)</label>
          <Input type="number" value={s.early_days} onChange={(e) => upd('early_days', e.target.value)} className="h-9" data-testid="season-early" />
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[11px] text-slate-500 mr-1">Products:</span>
        {CATEGORY_OPTIONS.map((c) => {
          const on = (s.categories || []).includes(c.value);
          return (
            <button
              key={c.value}
              onClick={() => toggleCat(c.value)}
              className={`text-[11px] px-2 py-1 rounded-full border transition ${on ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200'}`}
              data-testid={`season-cat-${c.value}`}
            >{c.label}</button>
          );
        })}
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={saving} data-testid="season-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save
        </Button>
      </div>
    </div>
  );
}

const MonthDay = ({ label, m, d, onM, onD }) => (
  <div className="col-span-1 sm:col-span-2">
    <label className="text-[11px] text-slate-500">{label}</label>
    <div className="flex gap-1.5">
      <select value={m} onChange={(e) => onM(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm flex-1">
        {MONTHS.map((mo, i) => <option key={mo} value={i + 1}>{mo}</option>)}
      </select>
      <Input type="number" min="1" max="31" value={d} onChange={(e) => onD(e.target.value)} className="h-9 w-16" />
    </div>
  </div>
);
